import {
  VIOLATION_AGENCIES,
  VIOLATION_AGENCY_LABELS,
  type ViolationAgency,
} from '@sitewise/shared';
import { Button, EmptyState, ErrorState, Skeleton, cn } from '@sitewise/ui';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, ArrowLeft, Building2, ChevronRight } from 'lucide-react';
import { Link, Navigate, useParams } from 'react-router-dom';

import { fetchAgencyBuildings } from '../../../api/violations.js';

function isAgency(value: string | undefined): value is ViolationAgency {
  return !!value && (VIOLATION_AGENCIES as readonly string[]).includes(value);
}

/**
 * `/violations/agency/:agency` — buildings with open violations for
 * one agency. Each card drills into that building's violation cards.
 */
export function ViolationAgencyPage() {
  const { agency: agencyParam } = useParams<{ agency: string }>();
  const agency = isAgency(agencyParam) ? agencyParam : null;

  const buildingsQuery = useQuery({
    queryKey: ['violations', 'agency', agency, 'buildings'],
    queryFn: () => fetchAgencyBuildings(agency!),
    enabled: agency != null,
  });

  if (!agency) {
    return <Navigate to="/violations" replace />;
  }

  const buildings = buildingsQuery.data?.buildings ?? [];
  const totalOpen = buildingsQuery.data?.totalOpen ?? 0;

  return (
    <div className="space-y-6 p-4">
      <div className="space-y-3">
        <Button variant="ghost" size="sm" asChild>
          <Link to="/violations">
            <ArrowLeft className="h-4 w-4" />
            All agencies
          </Link>
        </Button>
        <header>
          <h1 className="text-xl font-semibold">{VIOLATION_AGENCY_LABELS[agency]}</h1>
          <p className="text-sm text-fg-muted">
            Buildings with open {VIOLATION_AGENCY_LABELS[agency]} violations
            {buildingsQuery.data ? (
              <>
                {' '}
                · <span className="tabular-nums text-fg-secondary">{totalOpen}</span> open
              </>
            ) : null}
          </p>
        </header>
      </div>

      {buildingsQuery.isError ? (
        <ErrorState description="Could not load buildings for this agency." />
      ) : buildingsQuery.isLoading ? (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-24 w-full rounded-lg" />
          ))}
        </div>
      ) : buildings.length === 0 ? (
        <EmptyState
          icon={AlertTriangle}
          title={`No open ${VIOLATION_AGENCY_LABELS[agency]} violations`}
          description="Nothing open for this agency across your buildings right now."
        />
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {buildings.map((b) => (
            <Link
              key={b.buildingId}
              to={`/violations/agency/${agency}/buildings/${b.buildingId}`}
              className={cn(
                'group flex flex-col justify-between rounded-lg border border-border bg-surface p-4',
                'transition-colors hover:border-fg-muted hover:bg-surface-muted',
              )}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-fg-muted">
                    <Building2 className="h-3.5 w-3.5" />
                    Building
                  </div>
                  <div className="mt-1 truncate text-base font-semibold">{b.buildingName}</div>
                </div>
                <ChevronRight className="mt-1 h-4 w-4 shrink-0 text-fg-muted transition-transform group-hover:translate-x-0.5" />
              </div>
              <div className="mt-4 flex flex-wrap items-end justify-between gap-2">
                <div>
                  <div className="text-2xl font-semibold tabular-nums leading-none">
                    {b.openCount}
                  </div>
                  <div className="mt-1 text-xs text-fg-muted">
                    {b.openCount === 1 ? 'open violation' : 'open violations'}
                  </div>
                </div>
                {(b.criticalCount > 0 || b.highCount > 0) && (
                  <div className="text-right text-xs text-fg-muted">
                    {b.criticalCount > 0 ? (
                      <div className="text-danger tabular-nums">
                        {b.criticalCount} critical
                      </div>
                    ) : null}
                    {b.highCount > 0 ? (
                      <div className="tabular-nums">{b.highCount} high</div>
                    ) : null}
                  </div>
                )}
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
